// ============================================================
// TargetingConsole - the cockpit's targeter (v8.232.0).
//
// An interactive silhouette of the CURRENT target: click a zone to
// engage a Called Shot there (the -20, reduced by Precise Blow or
// Deadeye Shot, chambers into the trigger - see engine/actions/
// chamber.ts), click it again or the AUTO cap to stand down. One
// gesture owns both `attackToggles.calledShot` and `targetLocation`;
// removing the ledger's Called Shot chip instead leaves the location
// FORCED without the penalty - the console shows that state honestly.
//
// Bodies that cannot take a called shot render dimmed with a stencil
// saying why: hordes take hits as a mass, amorphous things have no
// vulnerable locations. A downed target is stencilled TARGET DOWN but
// stays clickable - finishing shots are a thing.
// ============================================================
import { getBodyConfig } from '../../engine/bodyConfig';
import { effectiveVehicleFacing } from '../../engine/vehicleFacing';
import { condType } from '../../engine/conditions';
import { TargetingSilhouette } from './TargetingSilhouette';
import styles from '../../styles/TargetingConsole.module.css';
import type { Target } from '../../types';
import { figureFor } from '../../engine/plate/figureFor';

interface Props {
  target: Target | null;
  /** 'auto' or a location id. May briefly hold an id from the PREVIOUS body type. */
  targetLocation: string;
  calledShot: boolean;
  calledShotReduction: number;
  onEngageZone: (locId: string) => void;
  onClear: () => void;
  disabled?: boolean;
  /** The target's own sheet, whose traits pick a person's figure (plan body-map P4). */
  figureSheet?: { traits?: { traitId?: string }[] } | null;
}

/**
 * The targeter. Instrument columns (v8.239.0): a 132px-tall silhouette at
 * the humanoid 0.545 aspect was ~72px wide, centred in the full console
 * width - the gutters were dead space. The art grows on the left and the
 * former gutter is the readout column.
 */
export default function TargetingConsole({
  target,
  targetLocation,
  calledShot,
  calledShotReduction,
  onEngageZone,
  onClear,
  disabled = false,
  figureSheet,
}: Props) {
  const bodyType = target?.bodyType || (target?.horde?.isHorde ? 'horde' : 'humanoid');
  const isHorde = bodyType === 'horde' || !!target?.horde?.isHorde;
  const config = getBodyConfig(bodyType);
  const penalty = Math.max(20 - calledShotReduction, 0);
  // A stale id from the previous body type reads as nothing selected -
  // DWS resets it an effect later.
  const selected = config.locations.some((l) => l.id === targetLocation) ? targetLocation : 'auto';
  const selectedName = config.locations.find((l) => l.id === selected)?.name ?? null;
  const armourFor = (locId: string) => armourReadout(target, bodyType, locId);
  const interactive = !!target && !isHorde && config.supportsCalledShot && !disabled;
  const verdict = stencilVerdict({
    target,
    isHorde,
    supportsCalledShot: config.supportsCalledShot,
    calledShot,
    selectedName,
  });

  return (
    <div className={styles.console} data-testid="targeting-console">
      <div className={styles.instrument}>
        <div className={styles.stage}>
          <TargetingSilhouette
            {...{ target, bodyType, isHorde, config, selected, interactive, penalty, armourFor, onEngageZone, onClear }}
            body={target ? figureFor(target, figureSheet ?? null) : null}
          />
          <DownStencil target={target} />
        </div>
        <div className={styles.readoutCol}>
          <div className={styles.readoutLine}>
            <Stencil verdict={verdict} penalty={penalty} />
          </div>
          {interactive && (
            <Readouts
              selected={selected}
              calledShot={calledShot}
              penalty={penalty}
              armourFor={armourFor}
              onClear={onClear}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A downed target is stencilled TARGET DOWN but stays clickable - finishing
 * shots are a thing. ON THE BODY, on two lines: one line is wider than the
 * art and hung off the console's left edge and the panel's (2026-09-09
 * playtest); the user wants it on the silhouette, so the words stack
 * instead of spreading.
 */
function DownStencil({ target }: { target: Target | null }) {
  if (!target || !(target.conditions ?? []).some((c: string) => condType(c) === 'K')) return null;
  return (
    <span className={styles.downStencil} data-testid="targeting-down-stencil">
      {'TARGET\nDOWN'}
    </span>
  );
}

/** Armour readout per zone - vehicles armour by FACING, not location. */
function armourReadout(target: Target | null, bodyType: string, locId: string): string {
  if (!target) return '';
  if (bodyType !== 'vehicle') return `armour ${target.armour?.[locId] ?? 0}`;
  const facing = effectiveVehicleFacing(target);
  return `armour ${target.armour?.[facing] ?? 0} (${facing} facing)`;
}

/** The stencil's tone, words, and whether the penalty follows them. */
interface Verdict {
  tone: 'stencilDim' | 'stencilArmed' | 'stencilForced';
  text: string;
  /** The penalty printed after the words, in its own span. */
  withPenalty?: boolean;
  /** The penalty as part of the words: "CALLED SHOT -20 - SELECT ZONE". */
  inlinePenalty?: boolean;
}

interface VerdictInput {
  target: Target | null;
  isHorde: boolean;
  supportsCalledShot: boolean;
  calledShot: boolean;
  selectedName: string | null;
}

/** The console's stencil verdict - one line saying what a shot will do. */
function stencilVerdict({ target, isHorde, supportsCalledShot, calledShot, selectedName }: VerdictInput): Verdict {
  if (!target) return { tone: 'stencilDim', text: 'NO TARGET' };
  if (isHorde) return { tone: 'stencilDim', text: 'MASS TARGET - HITS STRIKE THE HORDE' };
  if (!supportsCalledShot) return { tone: 'stencilDim', text: 'NO VULNERABLE LOCATIONS' };
  const zone = selectedName?.toUpperCase();
  if (calledShot && zone) return { tone: 'stencilArmed', text: `CALLED SHOT - ${zone}`, withPenalty: true };
  if (calledShot) return { tone: 'stencilArmed', text: 'CALLED SHOT', inlinePenalty: true };
  if (zone) return { tone: 'stencilForced', text: `LOCATION FORCED - ${zone}` };
  return { tone: 'stencilDim', text: 'AUTO - LOCATION BY ROLL' };
}

function Stencil({ verdict, penalty }: { verdict: Verdict; penalty: number }) {
  if (verdict.inlinePenalty) {
    return (
      <span className={styles[verdict.tone]}>
        {verdict.text} {-penalty} - SELECT ZONE
      </span>
    );
  }
  return (
    <span className={styles[verdict.tone]}>
      {verdict.text}
      {verdict.withPenalty && (
        <>
          {' '}
          <span className={styles.penalty}>{-penalty}</span>
        </>
      )}
    </span>
  );
}

interface ReadoutsProps {
  selected: string;
  calledShot: boolean;
  penalty: number;
  armourFor: (locId: string) => string;
  onClear: () => void;
}

/** The effective penalty, the selected zone's armour, and the AUTO cap that stands down. */
function Readouts({ selected, calledShot, penalty, armourFor, onClear }: ReadoutsProps) {
  const zoned = selected !== 'auto';
  return (
    <>
      {calledShot && (
        <div className={styles.readoutLine}>
          <span className={styles.stencilDim}>
            EFFECTIVE <span className={styles.penalty}>{-penalty}</span>
          </span>
        </div>
      )}
      {zoned && (
        <div className={styles.readoutLine}>
          <span className={styles.stencilDim}>{armourFor(selected).toUpperCase()}</span>
        </div>
      )}
      {(zoned || calledShot) && (
        <button type="button" className={styles.autoCap} data-testid="target-auto" onClick={onClear}>
          AUTO
        </button>
      )}
    </>
  );
}
